import { readSaTokenHeader } from '@glyphic/shared-core'

import { readApiBase } from '../api/config'
import type { AgentRunRequest } from '../api/agents'
import { getAuthToken } from './session'
import { dispatchSseBlock } from './sseEventStream'

function saTokenHeaderName(): string {
  return readSaTokenHeader(
    () => import.meta.env.VITE_SA_TOKEN_HEADER as string | undefined,
    { dev: import.meta.env.DEV },
  )
}

export type AgentRunStreamHandlers = {
  onEvent: (eventName: string, payload: unknown) => void
  onError?: (error: unknown) => void
  onDone?: () => void
}

export type AgentRunStreamHandle = {
  abort: () => void
  signal: AbortSignal
}

export type AgentConfirmStreamBody = {
  token: string
  approved?: boolean
  /** ask_user 交互工具专用：approved=true 时随恢复请求下发，与工具入参 questions 一一对应 */
  answers?: string[]
  runPublicId?: string | null
}

function openAgentSseStream(
  path: string,
  body: unknown,
  handlers: AgentRunStreamHandlers,
  errorLabel: string,
): AgentRunStreamHandle {
  const ac = new AbortController()
  let closed = false
  let finished = false

  const finish = () => {
    if (finished) return
    finished = true
    closed = true
    handlers.onDone?.()
  }

  // —— 按序重排 ——
  // 后端事件可能由不同线程发出（如 LLM 读流线程与 ReAct 循环线程），客户端读取
  // 变慢时（标签页切后台）线序可能与产生顺序不一致。每个事件带单调递增 seq，
  // 这里缓冲乱序事件、严格按 seq 顺序投递，保证时间线顺序稳定。
  let lastSeq = 0
  const pending = new Map<number, { eventName: string; payload: unknown }>()

  const deliver = (eventName: string, payload: unknown) => {
    handlers.onEvent(eventName, payload)
  }

  const deliverOrdered = (seq: number, eventName: string, payload: unknown) => {
    if (seq <= lastSeq) return
    if (seq === lastSeq + 1) {
      lastSeq = seq
      deliver(eventName, payload)
      let next = pending.get(lastSeq + 1)
      while (next) {
        pending.delete(lastSeq + 1)
        lastSeq += 1
        deliver(next.eventName, next.payload)
        next = pending.get(lastSeq + 1)
      }
      return
    }
    // 乱序到达：先缓冲，等前序事件补齐后再连续投递
    pending.set(seq, { eventName, payload })
    // 缓冲超限说明序号真的断了（无补发机制），跳过空洞按序放行，避免永久卡住
    if (pending.size > 32) {
      for (const key of [...pending.keys()].sort((a, b) => a - b)) {
        const item = pending.get(key)
        pending.delete(key)
        if (!item) continue
        lastSeq = key
        deliver(item.eventName, item.payload)
      }
    }
  }

  const isEnvelope = (value: unknown): value is { seq: number; data: unknown } =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { seq?: unknown }).seq === 'number' &&
    Number.isFinite((value as { seq: number }).seq) &&
    'data' in (value as object)

  void (async () => {
    const headers = new Headers({
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    })
    const token = getAuthToken()
    if (token) {
      headers.set(saTokenHeaderName(), token)
    }
    const base = readApiBase().replace(/\/+$/, '')
    const url = `${base}${path}`
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: ac.signal,
      })
      if (!res.ok || !res.body) {
        const message = `${errorLabel}（HTTP ${res.status}）`
        if (!closed) handlers.onError?.(new Error(message))
        finish()
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (!closed) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let splitAt = buffer.indexOf('\n\n')
        while (splitAt >= 0) {
          const block = buffer.slice(0, splitAt)
          buffer = buffer.slice(splitAt + 2)
          dispatchSseBlock(block, (eventName, data) => {
            if (closed) return
            try {
              const parsed = JSON.parse(data) as unknown
              if (isEnvelope(parsed)) {
                deliverOrdered(parsed.seq, eventName, parsed.data)
              } else {
                deliver(eventName, parsed)
              }
            } catch (error) {
              handlers.onError?.(error)
            }
          })
          splitAt = buffer.indexOf('\n\n')
        }
      }
    } catch (error) {
      if (!closed && !(error instanceof DOMException && error.name === 'AbortError')) {
        handlers.onError?.(error)
      }
    } finally {
      finish()
    }
  })()

  return {
    abort: () => {
      closed = true
      ac.abort()
      finish()
    },
    signal: ac.signal,
  }
}

/** POST `/v1/agents/runs/stream` 并解析 SSE 事件块 */
export function openAgentRunStream(
  body: AgentRunRequest,
  handlers: AgentRunStreamHandlers,
): AgentRunStreamHandle {
  return openAgentSseStream('/v1/agents/runs/stream', body, handlers, '智能体流式运行失败')
}

/** POST `/v1/agents/pending-actions/confirm/stream` 并解析 SSE 事件块 */
export function openAgentConfirmStream(
  body: AgentConfirmStreamBody,
  handlers: AgentRunStreamHandlers,
): AgentRunStreamHandle {
  return openAgentSseStream(
    '/v1/agents/pending-actions/confirm/stream',
    body,
    handlers,
    '智能体确认续跑失败',
  )
}
